import * as sdk from 'matrix-js-sdk'

import {saveMatrixSession} from '#/lib/storage/matrix'
import {CHAT_DEFAULT_HOMESERVER_URL} from '#/lib/constants'

const BASE_URL = CHAT_DEFAULT_HOMESERVER_URL
let currentBaseUrl = BASE_URL
const HIDDEN_ROOMS_ACCOUNT_DATA = 'app.tellus.hiddenRooms'

let client = sdk.createClient({
    useAuthorizationHeader: true,
    baseUrl: currentBaseUrl,
  })

client.baseUrl = currentBaseUrl

export default client
export {BASE_URL}

export function normalizeMatrixBaseUrl(baseUrl) {
  if (!baseUrl) return ''
  const trimmed = String(baseUrl).trim()
  if (!trimmed) return ''
  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`
  try {
    const parsed = new URL(withScheme)
    if (parsed.host === 'matrix.org') return 'https://matrix-client.matrix.org'
      return parsed.origin
  } catch {
    return ''
  }
}

function buildMatrixLoginPayload(username, password) {
  const trimmed = String(username || '').trim()
  if (!trimmed) return null

  const isEmail = trimmed.includes('@') && !trimmed.startsWith('@')
  if (isEmail) {
    return {
      password,
      identifier: {
        type: 'm.id.thirdparty',
        medium: 'email',
        address: trimmed,
      },
    }
  }

  const localpart =
    trimmed.startsWith('@') && trimmed.includes(':')
      ? trimmed.slice(1).split(':')[0]
      : trimmed
  if (!localpart) return null
  return {
    password,
    identifier: {type: 'm.id.user', user: localpart},
  }
}

export function getMatrixBaseUrl() {
  return currentBaseUrl
}

export function setMatrixBaseUrl(baseUrl) {
  const normalized = normalizeMatrixBaseUrl(baseUrl)
  if (!normalized) return client
  if (normalized === currentBaseUrl && client) {
    client.baseUrl = currentBaseUrl
    return client
  }
  currentBaseUrl = normalized
  try { client?.stopClient?.() } catch {}
  client = sdk.createClient({
    useAuthorizationHeader: true,
    baseUrl: currentBaseUrl,
  })
  client.baseUrl = currentBaseUrl
  return client
}

export function hs() {
  return (
    client.baseUrl ||
    (client.clientOpts && client.clientOpts.baseUrl) ||
    (client.opts && client.opts.baseUrl) ||
    currentBaseUrl ||
    ''
  )
}

export function me() {
  if (typeof client.getUserId === 'function') return client.getUserId()
  return (client.credentials && client.credentials.userId) || ''
}

export function listJoinedRooms() {
  const rooms = client.getRooms ? client.getRooms() : []
  const hidden = getHiddenRoomIds()
  return rooms.filter(
    r => r?.getMyMembership?.() === 'join' && !hidden.has(r?.roomId),
  )
}

export function listDmRooms() {
  const rooms = client.getVisibleRooms ? client.getVisibleRooms() : []
  const hidden = getHiddenRoomIds()
  return rooms.filter(
    r =>
      !hidden.has(r?.roomId) &&
      (r?.isDirectMessageRoom?.() || r?.getMyMembership?.() === 'join'),
  )
}

export function listIncomingInvites() {
  const rooms = client.getRooms ? client.getRooms() : []
  const hidden = getHiddenRoomIds()
  return rooms.filter(
    r => r?.getMyMembership?.() === 'invite' && !hidden.has(r?.roomId),
  )
}

export function listOutgoingPending() {
  const rooms = client.getVisibleRooms ? client.getVisibleRooms() : []
  const hidden = getHiddenRoomIds()
  return rooms.filter(r => {
    if (hidden.has(r?.roomId)) return false
    const membership = r?.getMyMembership?.()
    if (membership !== 'join') return false
    const joined = r?.getJoinedMembers?.() || []
    const invited = r?.getInvitedMembers?.() || []
    return invited.length > 0 && joined.length === 1
  })
}

function getHiddenRoomIds() {
  try {
    const data = client.getAccountData?.(HIDDEN_ROOMS_ACCOUNT_DATA)
    const content = data?.getContent?.() || data?.event?.content || data?.content
    const roomIds = content?.roomIds
    if (Array.isArray(roomIds)) {
      return new Set(roomIds)
    }
    if (roomIds && typeof roomIds === 'object') {
      return new Set(Object.keys(roomIds))
    }
  } catch {}
  return new Set()
}

export async function ensureDirectRoomWith(matrixUserId) {
  return ensureDirectRoom(matrixUserId)
}

export async function sendText(roomId, message) {
  return client.sendEvent(roomId, 'm.room.message', {
    msgtype: 'm.text',
    body: message,
  })
}

export async function sendTextMessage(roomId, message, {replyTo, editOf} = {}) {
  const content = {
    msgtype: 'm.text',
    body: message,
  }

  if (editOf) {
    content['m.new_content'] = {
      msgtype: 'm.text',
      body: message,
    }
    content['m.relates_to'] = {
      rel_type: 'm.replace',
      event_id: editOf,
    }
  } else if (replyTo) {
    content['m.relates_to'] = {
      'm.in_reply_to': {
        event_id: replyTo,
      },
    }
  }

  return client.sendEvent(roomId, 'm.room.message', content)
}

export async function sendReaction(roomId, eventId, key) {
  return client.sendEvent(roomId, 'm.reaction', {
    'm.relates_to': {
      rel_type: 'm.annotation',
      event_id: eventId,
      key,
    },
  })
}

export async function redactEvent(roomId, eventId, reason) {
  if (typeof client.redactEvent === 'function') {
    return client.redactEvent(roomId, eventId, undefined, reason)
  }
  return client.sendEvent(roomId, 'm.room.redaction', {
    redacts: eventId,
    reason,
  })
}

export async function uploadMediaContent(file, {name, mimeType} = {}) {
  const uploadName = name || 'upload'
  const uploadType = mimeType || (file && file.type) || undefined
  return client.uploadContent(file, {
    name: uploadName,
    type: uploadType,
    rawResponse: false,
  })
}

export async function sendMedia(
  roomId,
  file,
  {msgtype = 'm.file', name, mimeType, info, url, replyTo} = {},
) {
  let mxcUrl = url
  if (!mxcUrl) {
    if (!file) {
      throw new Error('No media to upload')
    }
    const upload = await uploadMediaContent(file, {name, mimeType})
    mxcUrl = upload?.content_uri || upload?.contentUri
  }
  if (!mxcUrl) {
    throw new Error('Media upload failed')
  }

  const content = {
    msgtype,
    body: name || 'Fichier',
    url: mxcUrl,
    info: {
      mimetype: mimeType,
      ...(info || {}),
    },
  }
  if (replyTo) {
    content['m.relates_to'] = {
      'm.in_reply_to': {
        event_id: replyTo,
      },
    }
  }

  return client.sendEvent(roomId, 'm.room.message', content)
}

export async function updateMyMatrixProfile({displayName, avatarFile} = {}) {
  if (displayName !== undefined && typeof client.setDisplayName === 'function') {
    await client.setDisplayName(displayName)
  }

  if (avatarFile) {
    const upload = await uploadMediaContent(avatarFile, {
      name: avatarFile?.name,
      mimeType: avatarFile?.type,
    })
    const mxcUrl = upload?.content_uri || upload?.contentUri
    if (mxcUrl && typeof client.setAvatarUrl === 'function') {
      await client.setAvatarUrl(mxcUrl)
    }
    return mxcUrl || null
  }

  return null
}

export function isMatrixLoggedIn() {
  try {
    const userId = client.getUserId && client.getUserId()
    return !!(userId && client && client.isLoggedIn)
  } catch {
    return false
  }
}

export function restoreMatrixSessionFromWebStorage({startClient = true} = {}) {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage?.getItem?.('matrix:session:v1')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const accessToken = parsed?.accessToken || parsed?.access_token
    const userId = parsed?.userId || parsed?.user_id
    const deviceId = parsed?.deviceId || parsed?.device_id

    if (!accessToken || !userId) return null

    const baseUrl =
      (typeof client?.getHomeserverUrl === 'function' && client.getHomeserverUrl()) ||
      client?.baseUrl ||
      currentBaseUrl ||
      BASE_URL

    const normalizedBaseUrl = normalizeMatrixBaseUrl(baseUrl) || BASE_URL
    currentBaseUrl = normalizedBaseUrl

    try { client?.stopClient?.() } catch {}
    client = sdk.createClient({
      baseUrl: normalizedBaseUrl,
      accessToken,
      userId,
      deviceId,
    })
    if (startClient) {
      client.startClient({pollTimeout: 30000})
    }
    return {accessToken, userId, deviceId}
  } catch {
    return null
  }
}
export function waitForInitialSync({timeoutMs = 15000} = {}) {
  return new Promise((resolve, reject) => {
    if (!client || typeof client.on !== 'function') {
      reject(new Error('Matrix client not initialized'))
      return
    }

      // ✅ Anti-race: si le client est déjà en état OK, on résout immédiatement
      try {
        const st = client.getSyncState?.()
        if (st === 'PREPARED' || st === 'SYNCING' || st === 'CATCHUP') {
          resolve(st)
          return
        }
        if (st === 'ERROR') {
          reject(new Error('Matrix sync error'))
          return
        }
      } catch {}
    let settled = false
    let timer = null
    const timeoutErr = new Error('Service temporairement indisponible. Réessayez.')
    timeoutErr.name = 'MatrixTimeout'

    const cleanup = () => {
      if (settled) return
      settled = true
      if (timer) clearTimeout(timer)
      try {
        client.removeListener?.('sync', onSync)
        client.off?.('sync', onSync)
      } catch {}
    }

    const onSync = (state, _prev, data) => {
      // States commonly seen: PREPARED, SYNCING, CATCHUP, ERROR, STOPPED
      if (state === 'ERROR') {
        cleanup()
        reject(data?.error || new Error('Matrix sync error'))
        return
      }
      if (state === 'PREPARED' || state === 'SYNCING' || state === 'CATCHUP') {
        cleanup()
        resolve(state)
      }
    }

    timer = setTimeout(() => {
      cleanup()
      reject(timeoutErr)
    }, timeoutMs)

    client.on('sync', onSync)
  })
}


export async function loginMatrix(
  username,
  password,
  {timeoutMs = 15000} = {},
) {
  try {
    const baseUrl =
      (typeof client.getHomeserverUrl === 'function' &&
        client.getHomeserverUrl()) ||
      client.baseUrl ||
      currentBaseUrl ||
      BASE_URL
    const normalizedBaseUrl = normalizeMatrixBaseUrl(baseUrl) || BASE_URL

    const timeoutErr = new Error(
      'Service temporairement indisponible. Réessayez.',
    )
    timeoutErr.name = 'MatrixTimeout'
    let timeoutId
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => reject(timeoutErr), timeoutMs)
    })

    let response
    try {
      const payload = buildMatrixLoginPayload(username, password)
      if (!payload) {
        throw new Error('Identifiant invalide.')
      }
      response = await Promise.race([
        client.login('m.login.password', payload),
        timeoutPromise,
      ])
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }

    const {access_token, user_id, device_id: deviceId} = response ?? {}
    if (!access_token || !user_id) {
      throw new Error('Réponse de connexion Tellus Chat invalide')
    }

    currentBaseUrl = normalizedBaseUrl
    try { client?.stopClient?.() } catch {}
      client = sdk.createClient({
    useAuthorizationHeader: true,
        baseUrl: normalizedBaseUrl,
        accessToken: access_token,
        userId: user_id,
        deviceId,
      })

    client.startClient({pollTimeout: 30000})


  // PERSIST_MATRIX_SESSION_LOGINWITHTOKEN
  // ✅ best effort : si quelqu'un appelle loginWithToken avec une session valide, on la persiste aussi
  try {
    Promise.resolve(
      saveMatrixSession({
        accessToken: access_token,
        userId: user_id,
        deviceId,
      }),
    ).catch(() => {})
  } catch {}

      // PERSIST_MATRIX_SESSION_LOGINMATRIX
      // ✅ rend /messages indépendant de /activate : la session est toujours sauvegardée ici
      try {
        await saveMatrixSession({
          accessToken: access_token,
          userId: user_id,
          deviceId,
        })
      } catch (e) {
        console.warn('Matrix: failed to persist session', e)
      }

      // LOCALSTORAGE_MATRIX_SESSION_FALLBACK
      // ✅ fallback web : si storage interne foire, on tente localStorage (sans jamais casser l'app)
      try {
        if (typeof window !== 'undefined' && window.localStorage?.setItem) {
          window.localStorage.setItem(
            'matrix:session:v1',
            JSON.stringify({accessToken: access_token, userId: user_id, deviceId}),
          )
        }
      } catch {}
      // ✅ Persist session for /messages
      try {
        await saveMatrixSession({accessToken: access_token, userId: user_id, deviceId})
      } catch {}
    return {access_token, user_id, device_id: deviceId}
  } catch (err) {
    console.error('Erreur de connexion Tellus Chat :', err)
    throw err
  }
}

export function loginWithToken({access_token, user_id, device_id: deviceId}) {
  if (!access_token || !user_id) {
    throw new Error('Session Tellus Chat invalide')
  }

  const baseUrl =
    (typeof client.getHomeserverUrl === 'function' &&
      client.getHomeserverUrl()) ||
    client.baseUrl ||
    currentBaseUrl ||
    BASE_URL
  const normalizedBaseUrl = normalizeMatrixBaseUrl(baseUrl) || BASE_URL

  currentBaseUrl = normalizedBaseUrl
  try { client?.stopClient?.() } catch {}
  client = sdk.createClient({
    useAuthorizationHeader: true,
      baseUrl: normalizedBaseUrl,
      accessToken: access_token,
      userId: user_id,
      deviceId,
    })

  client.startClient({pollTimeout: 30000})

  return client
}

export function listInvites() {
  return listIncomingInvites()
}

export async function acceptInvite(roomId) {
  await client.joinRoom(roomId)
}

export async function rejectInvite(roomId) {
  await client.leave(roomId)
}

export async function cancelOutgoing(roomId) {
  await client.leave(roomId)
}

export async function ensureDirectRoom(matrixUserId) {
  const rooms =
    (client.getVisibleRooms && client.getVisibleRooms()) ||
    (client.getRooms && client.getRooms()) ||
    []
  for (const r of rooms) {
    if (!r) continue
    const isDM = r.isDirectMessageRoom?.()
    const joinedMembers = r.getJoinedMembers?.() || []
    const hasJoined = joinedMembers.some(m => m && m.userId === matrixUserId)
    const members = r.getMembers?.() || []
    const hasMember = members.some(m => m && m.userId === matrixUserId)
    if (isDM && (hasJoined || hasMember)) {
      return r.roomId
    }
  }
  const res = await client.createRoom({
    is_direct: true,
    invite: [matrixUserId],
    preset: 'trusted_private_chat',
  })
  return res.room_id
}

export async function createGroupRoom({name, topic, invite}) {
  const payload = {
    is_direct: false,
    preset: 'private_chat',
    name,
  }
  if (topic) {
    payload.topic = topic
  }
  if (invite && invite.length) {
    payload.invite = invite
  }
  const res = await client.createRoom(payload)
  return res.room_id
}

export async function inviteToRoom(roomId, matrixUserId) {
  return client.invite(roomId, matrixUserId)
}

export async function leaveRoom(roomId) {
  return client.leave(roomId)
}

export function getClient() {
  return client
}

export async function logoutMatrix() {
  try {
    if (typeof client.stopClient === 'function') {
      client.stopClient()
    }
    if (typeof client.logout === 'function') {
      await client.logout()
    }
  } catch (err) {
    console.error('Erreur de déconnexion Tellus Chat :', err)
  }

  try {
    if (typeof client.clearStores === 'function') {
      await client.clearStores()
    }
  } catch (err) {
    console.warn('Impossible de nettoyer les stores Tellus Chat :', err)
  }

  client = sdk.createClient({
    useAuthorizationHeader: true,
    baseUrl: currentBaseUrl,
  })
  client.baseUrl = currentBaseUrl
  return client
}
